import { useEffect, useRef, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth-context";

export type ConversationType = "direct" | "group" | "announcement";
export type MessagePriority = "normal" | "high" | "urgent";
export type PresenceStatus = "online" | "away" | "offline";

export interface Conversation {
  id: string;
  type: ConversationType;
  title: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
}

export interface ConversationMember {
  id: string;
  conversation_id: string;
  user_id: string;
  role: "member" | "admin";
  last_read_at: string | null;
  muted: boolean;
  archived: boolean;
  joined_at: string;
}

export interface Message {
  id: string;
  conversation_id: string;
  sender_id: string;
  body: string;
  message_type: "text" | "system" | "announcement";
  priority: MessagePriority;
  reply_to_id: string | null;
  created_at: string;
  edited_at: string | null;
  deleted_at: string | null;
}

export interface MessageReaction {
  id: string;
  message_id: string;
  user_id: string;
  reaction: string;
}

export interface UserPresence {
  user_id: string;
  status: PresenceStatus;
  last_seen_at: string;
  updated_at: string;
}

export interface ConversationWithMeta extends Conversation {
  members: ConversationMember[];
  last_message: Message | null;
  unread_count: number;
}

export interface MessageWithMeta extends Message {
  sender_name: string;
  sender_role: string;
  reactions: MessageReaction[];
  reply_to: Message | null;
}

function formatTimestamp(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  const diff = (now.getTime() - d.getTime()) / 1000;
  if (diff < 60) return "Just now";
  if (diff < 3600) return `${Math.floor(diff / 60)} min`;
  if (diff < 86400) {
    const h = d.getHours().toString().padStart(2, "0");
    const m = d.getMinutes().toString().padStart(2, "0");
    return `Today, ${h}:${m}`;
  }
  if (diff < 172800) return "Yesterday";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export { formatTimestamp };

export function useConversations() {
  const { user } = useAuth();
  const [conversations, setConversations] = useState<ConversationWithMeta[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchConversations = useCallback(async () => {
    if (!user) return;
    const { data: memberships } = await supabase
      .from("conversation_members")
      .select("conversation_id, last_read_at, muted, archived")
      .eq("user_id", user.id);

    if (!memberships || memberships.length === 0) {
      setConversations([]);
      setLoading(false);
      return;
    }

    const convIds = memberships.map((m: any) => m.conversation_id);
    const { data: convs } = await supabase
      .from("conversations")
      .select("*")
      .in("id", convIds)
      .order("updated_at", { ascending: false });

    if (!convs) {
      setConversations([]);
      setLoading(false);
      return;
    }

    const { data: allMembers } = await supabase
      .from("conversation_members")
      .select("*")
      .in("conversation_id", convIds);

    const result: ConversationWithMeta[] = [];
    for (const conv of convs) {
      const members = (allMembers || []).filter((m: any) => m.conversation_id === conv.id);
      const myMembership = memberships.find((m: any) => m.conversation_id === conv.id);
      const { data: lastMsg } = await supabase
        .from("messages")
        .select("*")
        .eq("conversation_id", conv.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      let unread = 0;
      if (myMembership?.last_read_at) {
        const { count } = await supabase
          .from("messages")
          .select("*", { count: "exact", head: true })
          .eq("conversation_id", conv.id)
          .gt("created_at", myMembership.last_read_at)
          .neq("sender_id", user.id);
        unread = count || 0;
      } else {
        const { count } = await supabase
          .from("messages")
          .select("*", { count: "exact", head: true })
          .eq("conversation_id", conv.id)
          .neq("sender_id", user.id);
        unread = count || 0;
      }

      result.push({
        ...conv,
        members,
        last_message: lastMsg as Message | null,
        unread_count: unread,
      });
    }
    setConversations(result);
    setLoading(false);
  }, [user]);

  useEffect(() => {
    fetchConversations();
    if (!user) return;

    const channel = supabase
      .channel("conversations-changes")
      .on("postgres_changes", { event: "*", schema: "public", table: "messages" }, () => {
        fetchConversations();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "conversation_members" }, () => {
        fetchConversations();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, fetchConversations]);

  return { conversations, loading, refetch: fetchConversations };
}

export function useMessages(conversationId: string | null) {
  const { user } = useAuth();
  const [messages, setMessages] = useState<MessageWithMeta[]>([]);
  const [loading, setLoading] = useState(true);
  const [typingUser, setTypingUser] = useState<string | null>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchMessages = useCallback(async () => {
    if (!conversationId || !user) {
      setMessages([]);
      setLoading(false);
      return;
    }
    const { data: msgs } = await supabase
      .from("messages")
      .select("*")
      .eq("conversation_id", conversationId)
      .order("created_at", { ascending: true })
      .limit(200);

    if (!msgs || msgs.length === 0) {
      setMessages([]);
      setLoading(false);
      return;
    }

    const senderIds = [...new Set(msgs.map((m: any) => m.sender_id))];
    const { data: profiles } = await supabase
      .from("profiles")
      .select("id, full_name")
      .in("id", senderIds);

    const { data: roles } = await supabase
      .from("user_roles")
      .select("user_id, role")
      .in("user_id", senderIds);

    const { data: reactions } = await supabase
      .from("message_reactions")
      .select("*")
      .in("message_id", msgs.map((m: any) => m.id));

    const replyIds = msgs.filter((m: any) => m.reply_to_id).map((m: any) => m.reply_to_id);
    let replyMessages: any[] = [];
    if (replyIds.length > 0) {
      const { data: replies } = await supabase
        .from("messages")
        .select("*")
        .in("id", replyIds);
      replyMessages = replies || [];
    }

    const result: MessageWithMeta[] = msgs.map((m: any) => {
      const profile = (profiles || []).find((p: any) => p.id === m.sender_id);
      const role = (roles || []).find((r: any) => r.user_id === m.sender_id);
      return {
        ...m,
        sender_name: profile?.full_name || "Unknown",
        sender_role: role?.role || "user",
        reactions: (reactions || []).filter((r: any) => r.message_id === m.id),
        reply_to: replyMessages.find((r: any) => r.id === m.reply_to_id) || null,
      };
    });

    setMessages(result);
    setLoading(false);
  }, [conversationId, user]);

  useEffect(() => {
    fetchMessages();
    if (!conversationId || !user) return;

    const channel = supabase
      .channel(`messages-${conversationId}`)
      .on("postgres_changes",
        { event: "INSERT", schema: "public", table: "messages", filter: `conversation_id=eq.${conversationId}` },
        () => { fetchMessages(); }
      )
      .on("postgres_changes",
        { event: "UPDATE", schema: "public", table: "messages", filter: `conversation_id=eq.${conversationId}` },
        () => { fetchMessages(); }
      )
      .on("postgres_changes",
        { event: "DELETE", schema: "public", table: "messages", filter: `conversation_id=eq.${conversationId}` },
        () => { fetchMessages(); }
      )
      .on("postgres_changes",
        { event: "*", schema: "public", table: "message_reactions" },
        () => { fetchMessages(); }
      )
      .on("broadcast", { event: "typing" }, (payload: any) => {
        if (payload.userId !== user.id) {
          setTypingUser(payload.userName);
          if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setTypingUser(null), 3000);
        }
      })
      .on("broadcast", { event: "stop-typing" }, (payload: any) => {
        if (payload.userId !== user.id) {
          setTypingUser(null);
        }
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, [conversationId, user, fetchMessages]);

  const markAsRead = useCallback(async () => {
    if (!conversationId || !user) return;
    await supabase
      .from("conversation_members")
      .update({ last_read_at: new Date().toISOString() })
      .eq("conversation_id", conversationId)
      .eq("user_id", user.id);
  }, [conversationId, user]);

  const sendMessage = useCallback(async (body: string, priority: MessagePriority = "normal", replyToId: string | null = null) => {
    if (!conversationId || !user || !body.trim()) return;
    const { data, error } = await supabase
      .from("messages")
      .insert({
        conversation_id: conversationId,
        sender_id: user.id,
        body: body.trim(),
        priority,
        reply_to_id: replyToId,
      })
      .select("*")
      .single();
    if (error) throw error;
    return data;
  }, [conversationId, user]);

  const deleteMessage = useCallback(async (messageId: string) => {
    await supabase
      .from("messages")
      .update({ deleted_at: new Date().toISOString() })
      .eq("id", messageId)
      .eq("sender_id", user!.id);
  }, [user]);

  const toggleReaction = useCallback(async (messageId: string, reaction: string) => {
    if (!user) return;
    const { data: existing } = await supabase
      .from("message_reactions")
      .select("id")
      .eq("message_id", messageId)
      .eq("user_id", user.id)
      .eq("reaction", reaction)
      .maybeSingle();
    if (existing) {
      await supabase.from("message_reactions").delete().eq("id", existing.id);
    } else {
      await supabase.from("message_reactions").insert({
        message_id: messageId,
        user_id: user.id,
        reaction,
      });
    }
  }, [user]);

  const sendTyping = useCallback((userName: string) => {
    if (!conversationId) return;
    const channel = supabase.channel(`messages-${conversationId}`);
    channel.send({ type: "broadcast", event: "typing", payload: { userId: user?.id, userName } });
  }, [conversationId, user]);

  const stopTyping = useCallback(() => {
    if (!conversationId) return;
    const channel = supabase.channel(`messages-${conversationId}`);
    channel.send({ type: "broadcast", event: "stop-typing", payload: { userId: user?.id } });
  }, [conversationId, user]);

  return {
    messages,
    loading,
    typingUser,
    markAsRead,
    sendMessage,
    deleteMessage,
    toggleReaction,
    sendTyping,
    stopTyping,
    refetch: fetchMessages,
  };
}

export function usePresence() {
  const { user } = useAuth();
  const [presenceMap, setPresenceMap] = useState<Record<string, UserPresence>>({});
  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const updateMyPresence = useCallback(async (status: PresenceStatus) => {
    if (!user) return;
    await supabase
      .from("user_presence")
      .upsert({
        user_id: user.id,
        status,
        last_seen_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      });
  }, [user]);

  useEffect(() => {
    if (!user) return;
    updateMyPresence("online");
    heartbeatRef.current = setInterval(() => updateMyPresence("online"), 60000);

    const channel = supabase
      .channel("presence-changes")
      .on("postgres_changes",
        { event: "*", schema: "public", table: "user_presence" },
        (payload: any) => {
          setPresenceMap(prev => ({
            ...prev,
            [payload.new.user_id]: payload.new,
          }));
        }
      )
      .subscribe();

    const handleVisibility = () => {
      if (document.hidden) updateMyPresence("away");
      else updateMyPresence("online");
    };
    document.addEventListener("visibilitychange", handleVisibility);

    const handleBeforeUnload = () => updateMyPresence("offline");
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
      supabase.removeChannel(channel);
      document.removeEventListener("visibilitychange", handleVisibility);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      updateMyPresence("offline");
    };
  }, [user, updateMyPresence]);

  const getPresence = useCallback((userId: string): UserPresence | null => {
    return presenceMap[userId] || null;
  }, [presenceMap]);

  return { presenceMap, getPresence, updateMyPresence };
}

export function useUnreadMessageCount() {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const fetchCount = useCallback(async () => {
    if (!user) return;
    const { data: memberships } = await supabase
      .from("conversation_members")
      .select("conversation_id, last_read_at")
      .eq("user_id", user.id);

    if (!memberships || memberships.length === 0) {
      setUnreadCount(0);
      return;
    }

    let total = 0;
    for (const m of memberships) {
      let query = supabase
        .from("messages")
        .select("*", { count: "exact", head: true })
        .eq("conversation_id", m.conversation_id)
        .neq("sender_id", user.id);
      if (m.last_read_at) {
        query = query.gt("created_at", m.last_read_at);
      }
      const { count } = await query;
      total += count || 0;
    }
    setUnreadCount(total);
  }, [user]);

  useEffect(() => {
    fetchCount();
    if (!user) return;

    const channel = supabase
      .channel("unread-count")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "messages" }, () => {
        fetchCount();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "conversation_members" }, () => {
        fetchCount();
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, fetchCount]);

  return unreadCount;
}

export async function createConversation(
  creatorId: string,
  memberIds: string[],
  type: ConversationType = "direct",
  title: string | null = null
): Promise<string | null> {
  const { data: conv, error: convError } = await supabase
    .from("conversations")
    .insert({
      type,
      title,
      created_by: creatorId,
    })
    .select("id")
    .single();

  if (convError || !conv) return null;

  const members = memberIds.map(uid => ({
    conversation_id: conv.id,
    user_id: uid,
    role: uid === creatorId ? "admin" as const : "member" as const,
  }));

  const { error: memberError } = await supabase
    .from("conversation_members")
    .insert(members);

  if (memberError) return null;

  return conv.id;
}

export async function searchUsers(query: string, currentUserId: string): Promise<Array<{ id: string; full_name: string; role: string }>> {
  if (!query.trim()) return [];
  const { data: profiles } = await supabase
    .from("profiles")
    .select("id, full_name")
    .ilike("full_name", `%${query}%`)
    .neq("id", currentUserId)
    .limit(20);

  if (!profiles) return [];

  const userIds = profiles.map((p: any) => p.id);
  const { data: roles } = await supabase
    .from("user_roles")
    .select("user_id, role")
    .in("user_id", userIds);

  return profiles.map((p: any) => ({
    id: p.id,
    full_name: p.full_name,
    role: (roles || []).find((r: any) => r.user_id === p.id)?.role || "user",
  }));
}
